import { Component, Input, OnInit } from '@angular/core';

import { CompareValue } from '../compare-value/model';
import { CustomFormComponent } from '@services/formBuilder/abstract-custom-form-field';
import { CompareDurationValue } from './model';

import { TranslateService } from '@ngx-translate/core';
import { includes, isNumber } from 'lodash';
import { Options } from 'select2';
import { parse } from 'tinyduration';

interface InputData {
  min?: number;
  max?: number;
  id?: string;
}

@Component({
  selector: 'app-compare-duration',
  templateUrl: './compare-duration.component.html',
  styleUrls: ['./compare-duration.component.scss'],
})
export class CompareDurationComponent extends CustomFormComponent implements OnInit {
  @Input() inputData: InputData;

  public selectedOption: string;
  public isRange: boolean;

  // Min duration components (hours and minutes as numbers)
  public minHours = 0;
  public minMinutes = 0;

  // Max duration components (for range)
  public maxHours = 0;
  public maxMinutes = 0;

  public data = [
    { id: CompareValue.EqualTo, text: this.translate.instant('SHARED.Equal_to') },
    { id: CompareValue.NotEqualTo, text: this.translate.instant('SHARED.Not_equal_to') },
    { id: CompareValue.GreaterThan, text: this.translate.instant('SHARED.Greater_than') },
    { id: CompareValue.LessThan, text: this.translate.instant('SHARED.Less_than') },
    { id: CompareValue.GreaterThanEqualLessThanEqual, text: this.translate.instant('SHARED.Greater_than_equal_Less_than_equal') },
    { id: CompareValue.GreaterThanLessThan, text: this.translate.instant('SHARED.Greater_than_Less_than') }
  ];

  public selectOptions: Options = {
    minimumResultsForSearch: Infinity,
    placeholder: this.translate.instant('DASHPAGES.CHANGING_TYPE_TS_placeholder'),
    sorter: (data) => data,
    dropdownAutoWidth: true
  };

  private _internalFormValue: CompareDurationValue;
  private initialized = false;

  constructor(private translate: TranslateService) {
    super();
  }

  // Override formValue getter/setter to handle late initialization
  override get formValue(): CompareDurationValue {
    return this._internalFormValue;
  }

  override set formValue(value: CompareDurationValue) {
    this._internalFormValue = value;
    // Re-initialize UI when formValue is set after ngOnInit
    if (this.initialized && value) {
      this.initializeFromValue(value);
    }
    this.onChanged.emit(value);
  }

  public ngOnInit() {
    // Initialize from inputData first, then formValue (matching CompareValueComponent pattern)
    this.selectedOption = this.inputData?.id || this._internalFormValue?.id || CompareValue.GreaterThan;
    this.defineRangeOptions();

    // Use nullish coalescing to properly check each property individually
    const minValue = this.inputData?.min ?? this._internalFormValue?.min;
    const maxValue = this.inputData?.max ?? this._internalFormValue?.max;

    if (minValue !== undefined && minValue !== null) {
      this.parseDurationToComponents(minValue, true);
    }
    if (maxValue !== undefined && maxValue !== null) {
      this.parseDurationToComponents(maxValue, false);
    }

    this.initialized = true;
  }

  private initializeFromValue(value: InputData | CompareDurationValue): void {
    this.selectedOption = value?.id || CompareValue.GreaterThan;
    this.defineRangeOptions();

    if (value?.min !== undefined && value?.min !== null) {
      this.parseDurationToComponents(value.min, true);
    }
    if (value?.max !== undefined && value?.max !== null) {
      this.parseDurationToComponents(value.max, false);
    }
  }

  public onTypeChange(selection: { value: string }) {
    this.selectedOption = selection.value;
    this.defineRangeOptions();
    this.emitChanges();
  }

  // Input event handlers for numeric inputs
  public onMinHoursInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.minHours = parseInt(value, 10) || 0;
    this.emitChanges();
  }

  public onMinMinutesInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.minMinutes = parseInt(value, 10) || 0;
    this.emitChanges();
  }

  public onMaxHoursInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.maxHours = parseInt(value, 10) || 0;
    this.emitChanges();
  }

  public onMaxMinutesInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.maxMinutes = parseInt(value, 10) || 0;
    this.emitChanges();
  }

  // Validation on blur
  public validateMinHours(): void {
    if (this.minHours < 0) {
      this.minHours = 0;
      this.emitChanges();
    }
  }

  public validateMinMinutes(): void {
    if (this.minMinutes < 0) {
      this.minMinutes = 0;
    } else if (this.minMinutes > 59) {
      this.minMinutes = 59;
    }
    this.emitChanges();
  }

  public validateMaxHours(): void {
    if (this.maxHours < 0) {
      this.maxHours = 0;
      this.emitChanges();
    }
  }

  public validateMaxMinutes(): void {
    if (this.maxMinutes < 0) {
      this.maxMinutes = 0;
    } else if (this.maxMinutes > 59) {
      this.maxMinutes = 59;
    }
    this.emitChanges();
  }

  private emitChanges(): void {
    if (!this.selectedOption) {
      return;
    }

    const minMs = this.componentsToMilliseconds(this.minHours, this.minMinutes);
    const maxMs = this.componentsToMilliseconds(this.maxHours, this.maxMinutes);

    let newValue: CompareDurationValue;
    if (this.isRange && minMs >= 0 && maxMs >= 0) {
      // Ensure min <= max for range
      const actualMin = Math.min(minMs, maxMs);
      const actualMax = Math.max(minMs, maxMs);
      newValue = { id: this.selectedOption as CompareValue, min: actualMin, max: actualMax };
    } else if (minMs >= 0) {
      newValue = { id: this.selectedOption as CompareValue, min: minMs };
    } else {
      newValue = null;
    }

    // Update internal value and emit (without triggering re-initialization)
    this._internalFormValue = newValue;
    this.onChanged.emit(newValue);
  }

  private defineRangeOptions() {
    this.isRange = includes([
      CompareValue.GreaterThanEqualLessThanEqual,
      CompareValue.GreaterThanLessThan
    ], this.selectedOption);
  }

  private componentsToMilliseconds(hours: number, minutes: number): number {
    const h = hours || 0;
    const m = minutes || 0;

    return ((h * 60) + m) * 60 * 1000;
  }

  private parseDurationToComponents(value: number | string, isMin: boolean): void {
    let ms = 0;

    if (isNumber(value)) {
      ms = value;
    } else if (typeof value === 'string') {
      // Try parsing as ISO 8601 duration
      try {
        const duration = parse(value);
        // Convert days to hours for backward compatibility
        const hoursFromDays = ((duration.days ?? 0) + ((duration.weeks ?? 0) * 7) + ((duration.months ?? 0) * 30)) * 24;
        const hours = (duration.hours ?? 0) + hoursFromDays;
        const minutes = duration.minutes ?? 0;
        ms = ((hours * 60) + minutes) * 60 * 1000;
      } catch {
        // Not a valid ISO 8601 duration
        ms = parseInt(value, 10) || 0;
      }
    }

    // Convert ms to hours/minutes (days are converted to hours for backward compatibility)
    const totalMinutes = Math.floor(ms / (60 * 1000));
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (isMin) {
      this.minHours = hours;
      this.minMinutes = minutes;
    } else {
      this.maxHours = hours;
      this.maxMinutes = minutes;
    }
  }
}
